import React, { useContext, useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";

// Components
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import NavbarInner from "../components/Navbar-Inner";

import "./home.css";
import FooterNew from "../components/Footer-New";
import NavbarNew from "../components/Navbar-New";
import BackToTop from "../components/BackToTop";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

// import api
import { contactUsApi } from "../api/users.js";
import { toastAlert } from "../lib/toastAlert.js";


// Section fade-in animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 },
  }),
};

const servicesList = [
  {
    title: "Wealth Planning & Advisory",
    description:
      "Our wealth planning services take a holistic view of each client’s financial position, helping align assets, liabilities, income needs, investment objectives, and family priorities into a coherent long term strategy. We work closely with clients to structure financial decision making in a way that preserves flexibility while maintaining strategic discipline.\n\nWhether the priority is capital preservation, wealth accumulation, retirement readiness, or intergenerational transfer, our advisory process is rooted in practical planning and careful oversight.",
  },
  {
    title: "Retirement Planning",
    description:
      "Effective retirement planning requires more than asset accumulation. It requires disciplined forecasting, risk management, income structuring, and a portfolio designed to support financial independence across changing economic conditions.\n\nMG Asset Management develops retirement focused strategies that prioritize sustainability, inflation awareness, and stability of income. We help clients move from accumulation to preservation and income distribution with a framework designed to maintain confidence throughout retirement.",
  },
  {
    title: "Pension Solutions",
    description:
      "Pension focused investing demands a careful balance between growth, liability sensitivity, and long term risk control. Our pension solutions are designed for both individual and institutional contexts, providing investment frameworks that support continuity, resilience, and disciplined management of long duration financial obligations.\n\nWe build pension aware portfolios with a strong emphasis on asset allocation, diversification, income generation, and prudent governance.",
  },
  {
    title: "Discretionary Portfolio Management",
    description:
      "Our discretionary portfolio management service is designed for clients who want their capital actively overseen by experienced professionals within a clear strategic framework. We manage portfolios according to agreed objectives, risk tolerance, liquidity needs, and long term financial goals.\n\nThis approach allows for timely implementation, disciplined rebalancing, and ongoing portfolio refinement without requiring clients to make every day to day investment decision themselves.",
  },
  {
    title: "Advisory Portfolio Management",
    description:
      "For clients who prefer to remain closely involved in investment decisions, our advisory portfolio management service offers expert strategic guidance while preserving client control. We provide research, allocation advice, portfolio recommendations, and ongoing consultation to support informed decision making.\n\nThis model is suitable for sophisticated investors who value collaboration, market perspective, and structured advice within a long term framework.",
  },
  {
    title: "Multi Asset Portfolio Construction",
    description:
      "Diversification remains central to prudent long term wealth management. Our multi asset portfolio construction process incorporates a broad opportunity set across equities, fixed income, real estate, structured income, and alternative strategies, allowing us to design portfolios with stronger balance, resilience, and flexibility.\n\nBy allocating across asset classes with distinct risk and return characteristics, we aim to reduce concentration risk and support more stable long term outcomes.",
  },
  {
    title: "Income Focused Investment Strategies",
    description:
      "Many clients require portfolios that do more than grow capital. They require dependable income. Our income focused strategies are designed to generate stable cash flow through carefully selected combinations of dividend paying securities, fixed income instruments, structured credit, mortgage backed investments, and other income generating assets.\n\nThese strategies are built with a strong focus on quality, durability, and consistency of underlying cash flows.",
  },
  {
    title: "Capital Preservation Strategies",
    description:
      "Preserving capital is often as important as growing it. Our capital preservation strategies are designed for clients seeking a more conservative investment posture, particularly during periods of market uncertainty, transition, or wealth transfer planning.\n\nThese portfolios prioritize downside risk management, prudent diversification, liquidity awareness, and preservation of purchasing power over time.",
  },
  {
    title: "Hedge Fund Strategies",
    description:
      "MG Asset Management offers access to carefully selected hedge fund and hedge oriented strategies for clients seeking broader diversification and more sophisticated sources of return. These strategies may include long/short approaches, tactical allocation, macro aware positioning, and other risk managed methodologies designed to perform across a variety of market environments.\n\nOur approach to hedge related investing is disciplined and selective. The objective is not speculation, but portfolio enhancement through diversification, downside awareness, and exposure to differentiated return drivers.",
  },
  {
    title: "Real Estate Investments",
    description:
      "Real estate remains an important pillar of diversified wealth management. We incorporate real estate opportunities into broader portfolios where appropriate, focusing on assets and structures that can support long term capital appreciation, dependable income, and real asset backing.\n\nOur real estate exposure may include direct opportunities, real estate funds, income producing properties, and other carefully assessed property related investments aligned with client objectives and liquidity considerations.",
  },
  {
    title: "Mortgage Funds & Structured Credit",
    description:
      "Mortgage backed and structured credit strategies can provide compelling income opportunities supported by underlying collateral and disciplined underwriting. MG Asset Management incorporates mortgage fund utilization and related structured credit exposure as part of a broader income and diversification framework.\n\nWe focus on quality of collateral, risk controls, lending discipline, and sustainability of returns, with the aim of delivering dependable income and a stronger asset backed component within client portfolios.",
  },
  {
    title: "Private Market Investments",
    description:
      "Private market opportunities can offer long term value creation beyond public markets. For suitable clients, we provide access to selected private market strategies including private equity, private debt, and non listed investment structures that complement diversified wealth portfolios.\n\nThese opportunities are considered selectively and integrated thoughtfully, with close attention to liquidity, risk profile, time horizon, and overall portfolio role.",
  },
  {
    title: "Family Office Support",
    description:
      "For families with more complex financial structures, our family office oriented services provide a coordinated approach to investment oversight, asset visibility, strategic planning, and long term wealth continuity. We support families in maintaining structure and clarity across multiple asset categories and financial priorities.\n\nThis service is particularly relevant for clients seeking a more comprehensive and relationship driven approach to long term capital oversight.",
  },
  {
    title: "Institutional Investment Solutions",
    description:
      "We work with institutional and professionally managed capital seeking disciplined investment governance, diversified portfolio construction, and access to broader strategies beyond conventional mandates. Our institutional solutions are designed with an emphasis on structure, oversight, consistency, and alignment with long term obligations.",
  },
];

const serveItems = [
  {
    title: "High Net Worth Individuals",
    description:
      "Clients seeking personalized portfolio management, wealth preservation, retirement structuring, and access to broader investment solutions.",
  },
  {
    title: "Families and Multi Generational Wealth",
    description:
      "Families looking to protect and transfer wealth responsibly while maintaining clarity, discipline, and long term continuity.",
  },
  {
    title: "Pension Focused Clients",
    description:
      "Clients and organizations requiring investment approaches aligned with long term liabilities, income needs, and prudent governance.",
  },
  {
    title: "Institutional Investors",
    description:
      "Professional and institutional clients seeking diversified mandates, disciplined oversight, and access to both traditional and alternative strategies.",
  },
  {
    title: "Family Offices",
    description:
      "Families or structures requiring broader oversight across multiple asset types, strategic priorities, and long term capital objectives.",
  },
];

export default function Home() {
  const { isAuth } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [activeServiceImage, setActiveServiceImage] = useState(-1);
  const [activeServiceIndex, setActiveServiceIndex] = useState(0);
  const [activeAccordionIndex, setActiveAccordionIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  const serviceRefsArray = useRef([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const { t } = useTranslation();
  const [consultationForm, setConsultationForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [consultationErrors, setConsultationErrors] = useState({});
  const [loader, setLoader] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { name, email, phone, message } = consultationForm;

  const handleConsultationChange = (e) => {
    const { name, value } = e.target;
    setConsultationForm({ ...consultationForm, [name]: value });
    if (consultationErrors[name]) {
      setConsultationErrors({ ...consultationErrors, [name]: "" });
    }
  };

  const handleConsultationSubmit = async () => {
    setLoader(true);
    const { status, message, errors } = await contactUsApi(consultationForm);
    if (status === "success") {
      setLoader(false);
      setShowSuccessModal(true);
      setConsultationForm({ name: "", email: "", phone: "", message: "" });
    } else {
      setLoader(false);
      setConsultationErrors(errors || {});
      if (message) toastAlert("error", t(message), "consultation");
    }
  };

  const handleServiceClick = (index) => {
    setActiveServiceIndex(index);
    setActiveServiceImage(index);
    // Scroll the mobile list to keep active item visible
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;
      const buttons = scrollContainer.querySelectorAll("button");
      if (buttons[index]) {
        buttons[index].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
    // Scroll to the service in the right content area
    if (serviceRefsArray.current[index]) {
      serviceRefsArray.current[index].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === "left" ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  // Intersection Observer to sync scroll with left sidebar
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px 0px -50% 0px",
      threshold: 0.1,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = serviceRefsArray.current.indexOf(entry.target);
          if (index !== -1) {
            setActiveServiceIndex(index);
            setActiveServiceImage(index);
          }
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    serviceRefsArray.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      serviceRefsArray.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleInitialImage = () => {
      if (window.innerWidth < 1024) {
        setActiveServiceImage(0);
      }
    };

    handleInitialImage();
    window.addEventListener("resize", handleInitialImage);

    // Check scroll state for mobile
    setTimeout(handleScroll, 100);

    return () => window.removeEventListener("resize", handleInitialImage);
  }, []);
   useEffect(() => {
      document.title = "Home - MG Asset Management";
    }, []);
  
  return (
    <main className="hp_page">
    

      {/* Navbar */}
      <motion.div
        initial={{ y: "-100%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        style={{ position: "relative", zIndex: 1000 }}
      >
        {isAuth ? <NavbarInner /> : <NavbarNew />}
      </motion.div>

      {/* Section 1 */}
      <section className="hp_hero">
        <div className="container">
          <motion.div
            className="hp_hero_content"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.18 } } }}
          >
            <motion.div className="hp_hero_tags" variants={fadeUp}>
              <div className="hp_hero_tags_container">
                <div className="hp_hero_tags_content gradient_border_tags">
                  Family Run Stewardship • Retirement Solutions • Advanced
                  Investment Strategies
                </div>
              </div>
            </motion.div>

            <motion.h1 className="hp_hero_title" variants={fadeUp}>
              Preserving wealth. Securing futures. Built for legacy.
            </motion.h1>

            <motion.div className="hp_hero_body" variants={fadeUp}>
              <p>
                MG Asset Management is a family run asset management firm
                dedicated to protecting and growing capital through disciplined
                investment management, retirement planning, pension focused
                solutions, and advanced wealth strategies.
              </p>
              <p>
                We serve individuals, families, and institutions seeking
                long term financial stability, sophisticated portfolio
                construction, and trusted stewardship across both traditional
                and alternative asset classes.
              </p>
            </motion.div>

            <motion.div className="hp_hero_actions" variants={fadeUp}>
              <button
                className="primary_btn"
                onClick={() => navigate("/contact")}
              >
                Request a Consultation
              </button>
              <button
                className="primary_btn_outline"
                onClick={() => {
                  const servicesSection = document.getElementById("services");
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                Explore Our Services
              </button>
            </motion.div>

            <motion.p className="hp_hero_tagline" variants={fadeUp}>
              Long term, risk aware investment discipline
            </motion.p>
          </motion.div>
        </div>

        {/* Hero image full width */}
        <motion.div
          className="hp_hero_img_wrap"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={require("../assets/images/home/banner_bg.png")}
            alt="Image"
            className="img-fluid w-full"
          />
          <div className="banner_img">
            <img
              src={require("../assets/images/home/banner_img.png")}
              alt="Image"
              className="img-fluid m-auto"
            />
          </div>
        </motion.div>
      </section>

      {/* Section 2 */}
      <section className="w-full px-4 sm:px-6 md:px-8 lg:px-[100px] py-8 sm:py-16 md:py-24 lg:py-32 flex flex-col justify-start items-center gap-2.5">
        <div className="w-full max-w-[1720px] mx-auto flex flex-col justify-start items-start gap-8 sm:gap-12 md:gap-16 lg:gap-[100px]">
          <div className="w-full flex flex-col lg:flex-row justify-start items-start gap-6 md:gap-8 lg:gap-12">
            <div className="w-full lg:w-1/2 justify-center text-[#395645] text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[64px] font-normal font-['Forum']">
              A Trusted Approach to Modern Wealth Management
            </div>
            <div className="w-full lg:w-1/2 flex flex-col lg:flex-row justify-start lg:justify-between items-start lg:items-end gap-6 md:gap-8">
              <div className="w-full lg:flex-1 justify-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-[27px]">
                Our philosophy combines the personal accountability of a
                family run firm with the rigor of institutional investment
                discipline. We build portfolios and long term financial
                strategies designed to preserve wealth, generate sustainable
                growth, and support generational continuity.
              </div>
              <img
                src={require("../assets/images/home/img_01.png")}
                alt="Image"
                className="img-fluid"
              />
            </div>
          </div>
          <div className="w-full flex flex-col md:flex-row justify-start items-start gap-6 md:gap-8 lg:gap-12">
            <div className="w-full md:flex-1 self-stretch md:pr-8 lg:pr-12 border-b md:border-b-0 md:border-r border-[#395645]/20 flex flex-col justify-start items-start gap-6 md:gap-8 lg:gap-12 pb-6 md:pb-0">
              <div data-svg-wrapper className="relative flex-shrink-0">
                <img
                  src={require("../assets/images/home/icon_01.svg").default}
                  alt="Image"
                  className="img-fluid"
                />
              </div>
              <div className="self-stretch justify-center text-[#395645] text-xl sm:text-2xl font-normal font-['Roobert']">
                Private Client Solutions
              </div>
              <div className="self-stretch justify-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-[27px]">
                Tailored wealth planning, retirement structuring, and
                discretionary investment management.
              </div>
            </div>
            <div className="w-full md:flex-1 self-stretch md:pr-8 lg:pr-12 border-b md:border-b-0 md:border-r border-[#395645]/20 flex flex-col justify-start items-start gap-6 md:gap-8 lg:gap-12 pb-6 md:pb-0">
              <div data-svg-wrapper className="relative flex-shrink-0">
                <img
                  src={require("../assets/images/home/icon_02.svg").default}
                  alt="Image"
                  className="img-fluid"
                />
              </div>
              <div className="self-stretch justify-center text-[#395645] text-xl sm:text-2xl font-normal font-['Roobert']">
                Institutional Perspective
              </div>
              <div className="self-stretch justify-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-[27px]">
                Pension aware portfolio construction, disciplined governance,
                and diversified multi asset exposure.
              </div>
            </div>
            <div className="w-full md:flex-1 flex flex-col justify-start items-start gap-6 md:gap-8 lg:gap-12">
              <div data-svg-wrapper className="relative flex-shrink-0">
                <img
                  src={require("../assets/images/home/icon_03.svg").default}
                  alt="Image"
                  className="img-fluid"
                />
              </div>
              <div className="self-stretch justify-center text-[#395645] text-xl sm:text-2xl font-normal font-['Roobert']">
                Advanced Investment Capability
              </div>
              <div className="self-stretch justify-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-[27px]">
                Hedge fund strategies, real estate investments, mortgage funds,
                and structured income opportunities designed to strengthen
                portfolio resilience.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 */}
      <section className="w-full px-4 sm:px-6 md:px-8 lg:px-[100px] py-8 sm:py-16 md:py-24 lg:py-32 relative bg-[#395645] flex flex-col justify-start items-center gap-2.5 overflow-hidden">
        <img
          className="w-full sm:w-3/4 lg:w-[1107px] h-auto lg:h-[896px] absolute right-0 lg:-right-72 top-0 lg:top-[-233px] opacity-75 mix-blend-screen"
          src={require("../assets/images/home/bg_01.png")}
        />
        <div className="w-full max-w-[1720px] mx-auto relative z-10 flex flex-col justify-start items-start gap-8 sm:gap-12 md:gap-16 lg:gap-[72px]">
          <div className="w-full lg:w-11/12 justify-center text-[#f4f3f3] text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[64px] font-normal font-['Forum'] leading-tight">
            Built on trust. Guided by discipline.
            <br />
            Focused on long term value.
          </div>
          <div className="w-full flex flex-col lg:flex-row justify-start items-start gap-8 md:gap-12 lg:gap-16">
            {/* Services List - Left Column */}
            <div
              className="w-full lg:w-1/2 flex flex-col justify-start items-start gap-6 sm:gap-8 md:gap-10 lg:gap-12"
              onMouseLeave={() => {
                if (window.innerWidth >= 1024) {
                  setActiveServiceImage(-1);
                }
              }}
            >
              {/* Service Item 1 */}
              <div
                className="w-full cursor-pointer"
                onMouseEnter={() => setActiveServiceImage(0)}
              >
                <div className="text-[#acc6b7] text-lg sm:text-2xl md:text-3xl lg:text-[40px] font-normal font-['Roobert'] hover:text-[#fff] transition-colors duration-300">
                  Retirement Planning
                </div>
              </div>

              {/* Service Item 2 */}
              <div
                className="w-full cursor-pointer"
                onMouseEnter={() => setActiveServiceImage(1)}
              >
                <div className="text-[#acc6b7] text-lg sm:text-2xl md:text-3xl lg:text-[40px] font-normal font-['Roobert'] hover:text-[#fff] transition-colors duration-300">
                  Pension Solutions
                </div>
              </div>

              {/* Service Item 3 */}
              <div
                className="w-full cursor-pointer"
                onMouseEnter={() => setActiveServiceImage(2)}
              >
                <div className="text-[#acc6b7] text-lg sm:text-2xl md:text-3xl lg:text-[40px] font-normal font-['Roobert'] hover:text-[#fff] transition-colors duration-300">
                  Asset &amp; Portfolio Management
                </div>
              </div>

              {/* Service Item 4 */}
              <div
                className="w-full cursor-pointer"
                onMouseEnter={() => setActiveServiceImage(3)}
              >
                <div className="text-[#acc6b7] text-lg sm:text-2xl md:text-3xl lg:text-[40px] font-normal font-['Roobert'] hover:text-[#fff] transition-colors duration-300">
                  Hedge Fund Strategies
                </div>
              </div>

              {/* Service Item 5 */}
              <div
                className="w-full cursor-pointer"
                onMouseEnter={() => setActiveServiceImage(4)}
              >
                <div className="text-[#acc6b7] text-lg sm:text-2xl md:text-3xl lg:text-[40px] font-normal font-['Roobert'] hover:text-[#fff] transition-colors duration-300">
                  Real Estate Investments
                </div>
              </div>

              {/* Service Item 6 */}
              <div
                className="w-full cursor-pointer"
                onMouseEnter={() => setActiveServiceImage(5)}
              >
                <div className="text-[#acc6b7] text-lg sm:text-2xl md:text-3xl lg:text-[40px] font-normal font-['Roobert'] hover:text-[#fff] transition-colors duration-300">
                  Mortgage Backed Income Solutions
                </div>
              </div>
            </div>

            {/* Image Display - Right Column */}
            <div className="w-full lg:w-1/2 flex items-center justify-center lg:justify-end h-64 sm:h-80 md:h-96 lg:h-[500px]">
              {activeServiceImage >= 0 && activeServiceImage < 6 && (
                <motion.div
                  key={activeServiceImage}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  className="w-full h-full"
                >
                  <img
                    src={require(
                      `../assets/images/home/trust_0${activeServiceImage + 1}.png`,
                    )}
                    alt={`Service Image ${activeServiceImage + 1}`}
                    className="w-full h-full object-cover rounded-xl shadow-2xl"
                  />
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 */}
      <section
        id="about"
        className="w-full px-4 sm:px-6 md:px-8 lg:px-[100px] py-12 sm:py-20 md:py-32 lg:py-40 relative overflow-hidden bg-cover bg-center bg-no-repeat z-10"
        style={{
          backgroundImage: `url(${require("../assets/images/home/bg_02.png")})`,
        }}
      >
        <div className="w-full max-w-[788px] inline-flex flex-col justify-start items-start gap-12 relative z-10">
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            <div className="size- p-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-[#395645]/20 inline-flex justify-center items-center gap-2">
              <div data-svg-wrapper>
                <svg
                  width="6"
                  height="6"
                  viewBox="0 0 6 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="3" cy="3" r="3" fill="#395645" />
                </svg>
              </div>
              <div className="text-center justify-center text-[#395645] text-base font-normal font-['Roobert']">
                About the Firm
              </div>
            </div>
            <div className="w-full lg:w-11/12 justify-center text-[#395645] text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[64px] font-normal font-['Forum'] leading-tight">
              A Family Legacy of Financial Stewardship
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            <div className="self-stretch justify-center text-[#3a4f66] text-lg font-normal font-['Roobert'] leading-[28.80px]">
              MG Asset Management was built on the principle that wealth should
              be managed with care, foresight, and responsibility. As a
              family run firm, we value trust, discretion, and long term
              relationships above short term results or passing market
              narratives.
            </div>
            <div className="self-stretch justify-center text-[#3a4f66] text-lg font-normal font-['Roobert'] leading-[28.80px]">
              Our role is not simply to allocate capital. It is to understand
              the broader financial picture of each client and provide
              structured solutions that support capital preservation,
              sustainable growth, income generation, and intergenerational
              wealth continuity.
            </div>
            <div className="self-stretch justify-center text-[#3a4f66] text-lg font-normal font-['Roobert'] leading-[28.80px]">
              We combine traditional asset management principles with broader
              investment capabilities across pensions, retirement focused
              planning, alternative strategies, real estate exposure, and
              mortgage backed structures. This allows us to construct portfolios
              that are not only diversified, but intentionally designed to serve
              the long term interests of private and institutional investors.
            </div>
          </div>

          <div className="mt-16 w-full max-w-[850px] pl-6 pr-3 py-4 relative bg-[#395645] inline-flex justify-start items-start gap-2.5 overflow-hidden z-1">
            <img
              className="top-0 right-0 absolute mix-blend-overlay blur-[0px]"
              src={require("../assets/images/home/bg_03.png")}
            />
            <div className="w-[537px] justify-center text-[#f4f3f3] text-xl font-normal font-['Roobert'] leading-[30px]">
              At MG Asset Management, stewardship is not a slogan. It is the
              foundation of every mandate, every portfolio, and every client
              relationship.
            </div>
          </div>
        </div>
      </section>

      {/* Section 5 - Services */}
      <section
        id="services"
        className="w-full px-4 sm:px-6 md:px-8 lg:px-[100px] py-12 sm:py-20 md:py-32 lg:py-40 flex flex-col justify-start items-center gap-2.5"
      >
        {/* Header */}
        <div className="w-full max-w-[1720px] mx-auto flex flex-col justify-start items-center gap-6 sm:gap-8 md:gap-12 lg:gap-16 mb-8 sm:mb-12 md:mb-16 lg:mb-20">
          <div className="flex flex-col justify-start items-center gap-2 sm:gap-3">
            <div className="px-3 sm:px-4 py-2 sm:py-3 rounded-full outline outline-1 outline-offset-[-1px] outline-[#395645]/20 inline-flex justify-center items-center gap-2">
              <div className="w-1.5 h-1.5 bg-[#395645] rounded-full" />
              <div className="text-center text-[#395645] text-xs sm:text-sm font-normal font-['Roobert']">
                Services
              </div>
            </div>
            <div className="w-full lg:w-11/12 justify-center text-[#395645] text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[64px] font-normal font-['Forum'] leading-tight text-center">
              Comprehensive Wealth and Asset Management Solutions
            </div>
          </div>
          <div className="w-full lg:w-3/4 text-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed">
            We provide a broad range of investment and advisory services
            designed to preserve wealth, optimize allocation, and deliver
            disciplined long term outcomes. Our service architecture is built to
            meet the needs of high net worth individuals, families,
            pension focused clients, and institutional investors seeking both
            stability and sophisticated market access.
          </div>
        </div>

        {/* Service Content - Two Column Layout */}
        <div className="w-full max-w-[1720px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 md:gap-12 lg:gap-16">
            {/* Desktop: Sticky vertical list */}
            <div className="hidden lg:flex lg:w-1/3 flex-col pt-8">
              <div className="sticky top-32 flex flex-col gap-2 pr-4">
                {servicesList.map((service, index) => (
                  <button
                    key={index}
                    onClick={() => handleServiceClick(index)}
                    className={`w-full px-3 py-2 text-left transition-all duration-300 flex items-center gap-2 ${
                      activeServiceIndex === index
                        ? "text-[#395645]"
                        : "bg-white text-[#585858]/50 hover:text-[#585858]"
                    }`}
                  >
                    {activeServiceIndex === index && (
                      <div className="w-1.5 h-1.5 bg-[#395645] rounded-full flex-shrink-0" />
                    )}
                    <div className="text-[16px] sm:text-[18px] md:text-[18px] font-medium font-['Roobert']">
                      {service.title}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile: Horizontal scrollable list with arrows */}
            <div className="lg:hidden w-full mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Left Arrow */}
                <button
                  onClick={() => scroll("left")}
                  disabled={!canScrollLeft}
                  className={`flex-shrink-0 p-2 rounded-lg transition-all duration-300 ${
                    canScrollLeft
                      ? "bg-[#395645] text-white hover:bg-[#395645]/90"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                  aria-label="Scroll left"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {/* Horizontal Scrollable Container */}
                <div
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className="flex-1 flex gap-2 overflow-x-auto scrollbar-hide scroll-smooth"
                  style={{ scrollBehavior: "smooth" }}
                >
                  {servicesList.map((service, index) => (
                    <button
                      key={index}
                      onClick={() => handleServiceClick(index)}
                      className={`flex-shrink-0 px-3 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 min-w-max ${
                        activeServiceIndex === index
                          ? "bg-[#395645] text-white"
                          : "bg-gray-100 text-[#585858] hover:bg-gray-200"
                      }`}
                    >
                      <div className="text-xs sm:text-sm font-medium font-['Roobert'] whitespace-nowrap">
                        {service.title}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Right Arrow */}
                <button
                  onClick={() => scroll("right")}
                  disabled={!canScrollRight}
                  className={`flex-shrink-0 p-2 rounded-lg transition-all duration-300 ${
                    canScrollRight
                      ? "bg-[#395645] text-white hover:bg-[#395645]/90"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                  aria-label="Scroll right"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Right Content Area - Full Display */}
            <div className="w-full lg:w-2/3 pt-8" ref={contentContainerRef}>
              {servicesList.map((service, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    if (el) serviceRefsArray.current[index] = el;
                  }}
                  className={`${
                    activeServiceIndex === index ? "block" : "hidden lg:block"
                  } pb-12 md:pb-16 lg:pb-20`}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col gap-6 sm:gap-8 md:gap-10"
                  >
                    {/* Service Title */}
                    <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-normal font-['Forum'] text-[#395645]">
                      {service.title}
                    </div>

                    {/* Service Image */}
                    <div className="w-full h-64 sm:h-80 md:h-96 lg:h-[480px] rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center">
                      {index < 14 ? (
                        <motion.img
                          key={`img-${index}`}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.4 }}
                          src={require(
                            `../assets/images/home/service_${String(index + 1).padStart(2, "0")}.png`,
                          )}
                          alt={service.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-3">
                          <div className="w-16 h-16 bg-[#395645]/10 rounded-lg flex items-center justify-center">
                            <svg
                              className="w-8 h-8 text-[#395645]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                          <p className="text-[#3a4f66] text-sm font-medium">
                            Image Coming Soon
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Service Description */}
                    <div className="flex flex-col gap-3 sm:gap-4">
                      {service.description
                        .split("\n\n")
                        .map((paragraph, idx) => (
                          <p
                            key={idx}
                            className="text-sm sm:text-base md:text-lg text-[#3a4f66] font-normal font-['Roobert'] leading-relaxed"
                          >
                            {paragraph}
                          </p>
                        ))}
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 6 */}
      <section className="w-full px-4 sm:px-6 md:px-8 lg:px-[100px] py-12 sm:py-20 md:py-32 lg:py-32 bg-[#395645]/5 flex flex-col justify-start items-center">
        <div className="w-full max-w-[1720px] mx-auto flex flex-col lg:flex-row justify-start items-start gap-8 sm:gap-12 md:gap-16 lg:gap-[107px]">
          <div className="w-full lg:w-auto flex flex-col justify-start items-start gap-12">
            <div className="w-full flex flex-col justify-start items-start gap-3">
              <div className="p-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-[#395645]/20 inline-flex justify-center items-center gap-2">
                <div data-svg-wrapper>
                  <svg
                    width="6"
                    height="6"
                    viewBox="0 0 6 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="3" cy="3" r="3" fill="#395645" />
                  </svg>
                </div>
                <div className="text-center text-[#395645] text-sm sm:text-base font-normal font-['Roobert']">
                  Advanced Investment Strategies
                </div>
              </div>
              <div className="w-full text-[#395645] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal font-['Forum'] leading-tight">
                Institutional Capability Across Traditional and Alternative
                Assets
              </div>
            </div>
            <div className="w-full flex flex-col justify-start items-start gap-3">
              <div className="w-full text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed">
                In today’s investment environment, prudent wealth management
                often requires more than conventional portfolio exposure. MG
                Asset Management complements traditional asset allocation with
                advanced strategies designed to broaden diversification, enhance
                income potential, and improve resilience across different market
                conditions.
              </div>
              <div className="w-full text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed">
                Our capabilities extend across hedge fund strategies, real
                estate backed opportunities, mortgage fund utilization,
                structured credit, and selective private market investments.
                These strategies are incorporated thoughtfully and only where
                they serve a clear role within the broader portfolio framework.
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="w-full lg:w-auto flex flex-col justify-start items-start gap-8 sm:gap-12">
            <div className="w-full pb-8 sm:pb-12 border-b border-[#395645]/20 flex flex-col justify-start items-start gap-6">
              <div className="w-full text-[#395645] text-xl sm:text-2xl font-normal font-['Roobert']">
                Hedge Fund Strategies
              </div>
              <div className="w-full text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed">
                We use hedge oriented and alternative strategies as part of a
                disciplined diversification model, focusing on risk managed
                exposure rather than speculative positioning. These approaches
                may support portfolio balance, reduce directional concentration,
                and provide access to differentiated market opportunities.
              </div>
            </div>
            <div className="w-full pb-8 sm:pb-12 border-b border-[#395645]/20 flex flex-col justify-start items-start gap-6">
              <div className="w-full text-[#395645] text-xl sm:text-2xl font-normal font-['Roobert']">
                Real Estate Assets
              </div>
              <div className="w-full text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed">
                Real estate offers both income and long term asset backed value.
                We integrate carefully selected real estate investments where
                appropriate to strengthen portfolio diversification and support
                real asset exposure aligned with long term objectives.
              </div>
            </div>
            <div className="w-full flex flex-col justify-start items-start gap-6">
              <div className="w-full text-[#395645] text-xl sm:text-2xl font-normal font-['Roobert']">
                Mortgage Fund Utilization
              </div>
              <div className="w-full text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed">
                Mortgage fund and structured lending strategies can provide
                dependable income supported by underlying collateral. When used
                with care and proper underwriting discipline, they can play a
                valuable role in income oriented and diversified portfolios.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7 */}
      <section
        id="approach"
        className="w-full px-4 sm:px-6 md:px-8 lg:px-[100px] py-12 sm:py-20 md:py-32 lg:py-32 "
      >
        <div className="max-w-[1476px] relative">
          {/* Header Content */}
          <div className="w-full max-w-[1044px] inline-flex flex-col justify-start items-start gap-12 mb-12">
            <div className="self-stretch flex flex-col justify-start items-start gap-3">
              <div className="size- p-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-[#395645]/20 inline-flex justify-center items-center gap-2">
                <div data-svg-wrapper>
                  <svg
                    width="6"
                    height="6"
                    viewBox="0 0 6 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="3" cy="3" r="3" fill="#395645" />
                  </svg>
                </div>
                <div className="text-center justify-center text-[#395645] text-base font-normal font-['Roobert']">
                  Our Approach
                </div>
              </div>
              <div className="w-full text-[#395645] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal font-['Forum'] leading-tight">
                Disciplined Process. Tailored Strategy. Ongoing Oversight.
              </div>
            </div>
            <div className="self-stretch justify-center text-[#3a4f66] text-lg font-normal font-['Roobert'] leading-[28.80px]">
              Every client relationship begins with understanding. From there,
              we develop and manage investment strategies through a clear and
              repeatable process designed to preserve discipline, adapt
              intelligently, and remain aligned with long term goals.
            </div>
          </div>

          {/* Main Content Grid with Sticky Image */}
          <div className="relative w-full">
            {/* Sticky Image Container */}
            <div className="hidden lg:block sticky top-[100px] h-fit flex-shrink-0 ml-[0px] z-0">
              <div className="w-[900px] h-[550px] rounded-sm overflow-hidden">
                <img
                  className="w-full h-full object-cover rounded-sm"
                  src={require("../assets/images/home/img_03.png")}
                  alt="Approach Image"
                />
              </div>
            </div>

            {/* Scrollable Content Container - Overlays on Image */}
            <div className="w-full lg:ml-0 flex flex-col justify-start items-start gap-0 relative z-10 lg:pl-[500px]">
              <div className="self-stretch p-9 bg-white outline outline-1 outline-offset-[-0.50px] outline-[#3a4f66]/10 flex flex-col justify-start items-start gap-6">
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="justify-center text-[#395645] text-2xl font-normal font-['Roobert']">
                    Discovery
                  </div>
                  <div className="h-[29px] px-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-[#3a4f66]/20 flex justify-center items-center gap-2">
                    <div data-svg-wrapper>
                      <svg
                        width="6"
                        height="6"
                        viewBox="0 0 6 6"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="3" cy="3" r="3" fill="#3a4f66" />
                      </svg>
                    </div>
                    <div className="text-center justify-center text-[#395645] text-sm md:text-base font-normal font-['Roobert'] text-nowrap">
                      Step 1
                    </div>
                  </div>
                </div>
                <div className="self-stretch justify-center text-[#395645] text-lg font-normal font-['Roobert'] leading-[27px]">
                  We begin by building a clear understanding of the client’s
                  objectives, time horizon, liquidity needs, family priorities,
                  and tolerance for risk. This stage establishes the strategic
                  foundation for all future planning and investment decisions.
                </div>
              </div>
              <div className="self-stretch p-9 bg-white outline outline-1 outline-offset-[-0.50px] outline-[#3a4f66]/10 flex flex-col justify-start items-start gap-6">
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="justify-center text-[#395645] text-2xl font-normal font-['Roobert']">
                    Strategy Design
                  </div>
                  <div className="h-[29px] px-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-[#395645]/25 flex justify-center items-center gap-2">
                    <div data-svg-wrapper>
                      <svg
                        width="6"
                        height="6"
                        viewBox="0 0 6 6"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="3" cy="3" r="3" fill="#395645" />
                      </svg>
                    </div>
                    <div className="text-center justify-center text-[#395645] text-sm md:text-base font-normal font-['Roobert'] text-nowrap">
                      Step 2
                    </div>
                  </div>
                </div>
                <div className="self-stretch justify-center text-[#3a4f66] text-lg font-normal font-['Roobert'] leading-[27px]">
                  Using the insights gathered during discovery, we construct a
                  tailored framework for wealth management and portfolio
                  allocation. This may include retirement planning, income
                  structuring, discretionary asset management, real estate
                  allocation, hedge strategy exposure, and other relevant
                  investment components.
                </div>
              </div>
              <div className="self-stretch p-9 bg-white outline outline-1 outline-offset-[-0.50px] outline-[#3a4f66]/10 flex flex-col justify-start items-start gap-6">
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="justify-center text-[#395645] text-2xl font-normal font-['Roobert']">
                    Implementation{" "}
                  </div>
                  <div className="h-[29px] px-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-[#395645]/25 flex justify-center items-center gap-2">
                    <div data-svg-wrapper>
                      <svg
                        width="6"
                        height="6"
                        viewBox="0 0 6 6"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="3" cy="3" r="3" fill="#395645" />
                      </svg>
                    </div>
                    <div className="text-center justify-center text-[#395645] text-sm md:text-base font-normal font-['Roobert'] text-nowrap">
                      Step 3
                    </div>
                  </div>
                </div>
                <div className="self-stretch justify-center text-[#3a4f66] text-lg font-normal font-['Roobert'] leading-[27px]">
                  We deploy capital through disciplined portfolio construction,
                  carefully selected investment vehicles, and a clear
                  understanding of each allocation’s purpose. Implementation is
                  deliberate, diversified, and aligned with both market
                  conditions and long term objectives.
                </div>
              </div>
              <div className="self-stretch p-9 bg-white outline outline-1 outline-offset-[-0.50px] outline-[#3a4f66]/10 flex flex-col justify-start items-start gap-6">
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="justify-center text-[#395645] text-2xl font-normal font-['Roobert']">
                    Oversight and Refinement{" "}
                  </div>
                  <div className="h-[29px] px-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-[#395645]/25 flex justify-center items-center gap-2">
                    <div data-svg-wrapper>
                      <svg
                        width="6"
                        height="6"
                        viewBox="0 0 6 6"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="3" cy="3" r="3" fill="#395645" />
                      </svg>
                    </div>
                    <div className="text-center justify-center text-[#395645] text-sm md:text-base font-normal font-['Roobert'] text-nowrap">
                      Step 4
                    </div>
                  </div>
                </div>
                <div className="self-stretch justify-center text-[#3a4f66] text-lg font-normal font-['Roobert'] leading-[27px]">
                  Markets evolve, and so do client circumstances. We provide
                  ongoing monitoring, rebalancing, and strategic review to
                  ensure each portfolio continues to reflect its intended
                  structure, risk profile, and long term purpose.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 8 */}
      <section className="w-full py-16 sm:py-24 md:py-32 flex flex-col justify-start items-center gap-6 md:gap-12 overflow-hidden">
        <div className="w-full max-w-[1333px] px-4 sm:px-6 md:px-8 flex flex-col justify-start items-center gap-12 md:gap-16 lg:gap-[100px]">
          <div className="w-full flex flex-col justify-start items-center gap-3 md:gap-4">
            <div className="size-auto p-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-[#395645]/20 inline-flex justify-center items-center gap-2">
              <div data-svg-wrapper>
                <svg
                  width="6"
                  height="6"
                  viewBox="0 0 6 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="3" cy="3" r="3" fill="#395645" />
                </svg>
              </div>
              <div className="text-center justify-center text-[#395645] text-xs sm:text-sm md:text-base font-normal font-['Roobert']">
                Why MG Asset Management
              </div>
            </div>
            <div className="w-full text-center justify-center text-[#395645] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal font-['Forum']">
              The Strength of Personal Trust with Institutional Discipline
            </div>
          </div>
          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 md:gap-6 lg:gap-8">
            <div className="flex flex-col justify-start items-start gap-6">
              <div data-svg-wrapper className="relative">
                <img
                  className="w-full h-full"
                  src={require("../assets/images/home/icon_04.svg").default}
                  alt="Approach Image"
                />
              </div>
              <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="self-stretch justify-center text-[#395645] text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-normal font-['Forum']">
                  Family Run Integrity
                </div>
                <div className="self-stretch justify-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed md:leading-[27px]">
                  As a family run firm, we value long term relationships,
                  personal accountability, and trusted stewardship. Clients are
                  not treated as account numbers, but as long term relationships
                  deserving of care and discretion.
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-start items-start gap-6">
              <div data-svg-wrapper className="relative">
                <img
                  className="w-full h-full"
                  src={require("../assets/images/home/icon_05.svg").default}
                  alt="Approach Image"
                />
              </div>
              <div className="self-stretch justify-center text-[#395645] text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-normal font-['Forum']">
                Independent Perspective
              </div>
              <div className="self-stretch justify-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed md:leading-[27px]">
                Our independence supports objective thinking and client first
                decision making. We are able to focus on strategy, suitability,
                and long term value without being driven by short term external
                pressures.
              </div>
            </div>
            <div className="flex flex-col justify-start items-start gap-6">
              <div data-svg-wrapper className="relative">
                <img
                  className="w-full h-full"
                  src={require("../assets/images/home/icon_06.svg").default}
                  alt="Approach Image"
                />
              </div>
              <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="self-stretch justify-center text-[#395645] text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-normal font-['Forum']">
                  Broad Investment Capability
                </div>
                <div className="self-stretch justify-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed md:leading-[27px]">
                  We combine traditional wealth and asset management services
                  with access to more advanced strategies including hedge funds,
                  real estate investments, and mortgage backed income solutions.
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-start items-start gap-6">
              <div data-svg-wrapper className="relative">
                <img
                  className="w-full h-full"
                  src={require("../assets/images/home/icon_07.svg").default}
                  alt="Approach Image"
                />
              </div>
              <div className="self-stretch justify-center text-[#395645] text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-normal font-['Forum']">
                Long Term Focus
              </div>
              <div className="self-stretch justify-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed md:leading-[27px]">
                Our investment philosophy is built on preservation, discipline,
                and sustainable growth rather than short lived themes or
                speculative narratives.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 9 */}
      <section
        id="clients"
        className="w-full px-4 sm:px-6 md:px-8 lg:px-[100px] py-16 sm:py-24 md:py-32 bg-[#395645] flex flex-col justify-start items-center gap-6 md:gap-12 overflow-hidden"
      >
        <div className="w-full max-w-[1720px] mx-auto flex flex-col lg:flex-row justify-start items-start gap-8 md:gap-12 lg:gap-16">
          {/* Left Section */}
          <div className="w-full lg:w-1/2 flex flex-col justify-start items-start gap-8 md:gap-12">
            <div className="w-full flex flex-col justify-start items-start gap-3">
              <div className="size-auto p-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-white/20 inline-flex justify-center items-center gap-2">
                <div data-svg-wrapper>
                  <svg
                    width="6"
                    height="6"
                    viewBox="0 0 6 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="3" cy="3" r="3" fill="white" />
                  </svg>
                </div>
                <div className="text-center justify-center text-white text-xs sm:text-sm md:text-base font-normal font-['Roobert']">
                  Who We Serve
                </div>
              </div>
              <div className="w-full justify-center text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal font-['Forum'] leading-tight">
                Structured for Sophisticated Clients and Long Term Capital
              </div>
            </div>
            <div className="w-full justify-center text-white text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed md:leading-[28.80px]">
              Our services are designed for individuals and institutions who
              value prudent strategy, portfolio sophistication, and a trusted
              long term relationship.
            </div>
          </div>

          {/* Right Section - Accordion */}
          <div className="w-full lg:w-1/2 flex flex-col justify-start items-start">
            {serveItems.map((item, index) => (
              <div
                key={index}
                className="w-full border-b border-white/20 flex flex-col justify-start items-start"
              >
                <button
                  onClick={() =>
                    setActiveAccordionIndex(
                      activeAccordionIndex === index ? -1 : index,
                    )
                  }
                  className="w-full py-6 md:py-8 flex flex-col justify-start items-start gap-4 md:gap-6 transition-all duration-300 hover:opacity-80"
                >
                  <div className="w-full inline-flex justify-between items-start gap-4">
                    <div className="justify-center text-white text-lg sm:text-xl md:text-2xl font-normal font-['Roobert'] text-left flex-1">
                      {item.title}
                    </div>
                    <motion.div
                      data-svg-wrapper
                      className="flex-shrink-0 mt-1"
                      animate={{
                        rotate: activeAccordionIndex === index ? 180 : 0,
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.99976 9.00005L11.9998 15L17.9998 9"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeMiterlimit="16"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </motion.div>
                  </div>
                </button>

                {/* Accordion Content - Smooth Animation */}
                <AnimatePresence initial={false}>
                  {activeAccordionIndex === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="w-full overflow-hidden"
                    >
                      <div className="pb-6 md:pb-8 text-white text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed md:leading-[27px]">
                        {item.description}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 10 */}
      <section
        id="insights"
        className="w-full px-4 sm:px-6 md:px-8 lg:px-[100px] py-16 sm:py-24 md:py-32 flex flex-col justify-start items-center gap-6 md:gap-12 overflow-hidden"
      >
        <div className="w-full max-w-[1720px] mx-auto flex flex-col justify-start items-start gap-12 md:gap-16 lg:gap-[60px]">
          {/* Header */}
          <div className="w-full flex flex-col justify-start items-start gap-12">
            <div className="w-full flex flex-col justify-start items-start gap-3">
              <div className="size-auto p-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-[#395645]/20 inline-flex justify-center items-center gap-2">
                <div data-svg-wrapper>
                  <svg
                    width="6"
                    height="6"
                    viewBox="0 0 6 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="3" cy="3" r="3" fill="#395645" />
                  </svg>
                </div>
                <div className="text-center justify-center text-[#395645] text-xs sm:text-sm md:text-base font-normal font-['Roobert']">
                  Insights
                </div>
              </div>
              <div className="w-full justify-center text-[#395645] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal font-['Forum']">
                Market Perspectives and Long Term Investment Thinking
              </div>
            </div>
            <div className="w-full justify-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed md:leading-[28.80px]">
              We believe informed investors make stronger long term decisions.
              Our insights section provides commentary, perspective, and
              research led thinking on wealth preservation, retirement planning,
              pension strategy, portfolio diversification, real estate exposure,
              hedge fund allocation, and broader market developments.
            </div>
          </div>

          {/* Cards Grid */}
          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-[60px]">
            {/* Card 1 */}
            <div className="flex flex-col justify-start items-start gap-6">
              <img
                className="w-full h-40 sm:h-48 md:h-56 lg:h-[280px] rounded-lg object-cover"
                src={require("../assets/images/home/service_02.png")}
                alt="Retirement Planning"
              />
              <div className="flex flex-col justify-start items-start gap-6 w-full">
                <div className="w-full justify-center text-[#395645] text-xl sm:text-2xl md:text-[28px] font-normal font-['Roobert']">
                  Planning for Retirement in a Changing Rate Environment
                </div>
                <Link to="/retirement-planning" className="primary_btn_outline">Read More</Link>
              </div>
            </div>

            {/* Card 2 */}
            <div className="flex flex-col justify-start items-start gap-6">
              <img
                className="w-full h-40 sm:h-48 md:h-56 lg:h-[280px] rounded-lg object-cover"
                src={require("../assets/images/home/service_05.png")}
                alt="Real Estate Portfolio"
              />
              <div className="flex flex-col justify-start items-start gap-6 w-full">
                <div className="w-full justify-center text-[#395645] text-xl sm:text-2xl md:text-[28px] font-normal font-['Roobert']">
                  The Role of Real Estate in Long Term Portfolio Construction
                </div>
                <button className="primary_btn_outline">Read More</button>
              </div>
            </div>

            {/* Card 3 */}
            <div className="flex flex-col justify-start items-start gap-6">
              <img
                className="w-full h-40 sm:h-48 md:h-56 lg:h-[280px] rounded-lg object-cover"
                src={require("../assets/images/home/service_11.png")}
                alt="Mortgage Strategies"
              />
              <div className="flex flex-col justify-start items-start gap-6 w-full">
                <div className="w-full justify-center text-[#395645] text-xl sm:text-2xl md:text-[28px] font-normal font-['Roobert']">
                  How Mortgage Backed Strategies Can Support Income Focused
                  Portfolios
                </div>
                <button className="primary_btn_outline">Read More</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 11 */}
      <section
        id="contact"
        className="w-full px-4 sm:px-6 md:px-8 lg:px-[100px] py-16 sm:py-24 md:py-32 bg-[#f4f3f3] flex flex-col justify-start items-center gap-6 md:gap-12 overflow-hidden"
      >
        <div className="w-full max-w-[1589px] mx-auto flex flex-col lg:flex-row justify-start items-center gap-8 md:gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="w-full lg:w-1/2 flex flex-col justify-start items-start gap-8 md:gap-12">
            <div className="w-full flex flex-col justify-start items-start gap-3">
              <div className="size-auto p-3 rounded-[50px] outline outline-1 outline-offset-[-1px] outline-[#395645]/20 inline-flex justify-center items-center gap-2">
                <div data-svg-wrapper>
                  <svg
                    width="6"
                    height="6"
                    viewBox="0 0 6 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="3" cy="3" r="3" fill="#395645" />
                  </svg>
                </div>
                <div className="text-center justify-center text-[#395645] text-xs sm:text-sm md:text-base font-normal font-['Roobert']">
                  Contact
                </div>
              </div>
              <div className="w-full justify-center text-[#395645] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal font-['Forum'] leading-tight">
                Begin the Conversation About Your Long Term Financial Strategy
              </div>
            </div>
            <div className="w-full justify-center text-[#3a4f66] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed md:leading-[28.80px]">
              Whether your priorities include retirement planning,
              pension focused investing, discretionary portfolio management,
              hedge fund exposure, real estate backed opportunities, or mortgage
              fund utilization, MG Asset Management is positioned to help you
              build a disciplined path forward.
            </div>
            <button
              className="primary_btn"
              onClick={() => navigate("/contact")}
            >
              Contact Our Team
            </button>
          </div>

          {/* Right Image */}
          <div className="w-full lg:w-1/2 h-full lg:h-full lg:min-h-[400px]">
            <img
              className="w-full h-full rounded-lg object-cover"
              src={require("../assets/images/home/img_04.png")}
              alt="Contact"
            />
          </div>
        </div>
      </section>

      {/* Section 12 */}
      <section className="w-full px-4 sm:px-6 md:px-8 lg:px-[100px] py-12 sm:py-16 md:py-24 lg:py-20 relative bg-[#395645] overflow-hidden">
        <div className="w-full max-w-[1720px] mx-auto flex flex-col lg:flex-row justify-between items-start gap-8 md:gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="w-full xl:w-2/5 flex flex-col justify-start items-start gap-6 md:gap-12 relative z-10">
            <div className="w-full flex flex-col justify-start items-start gap-3">
              <div className="w-full justify-center text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal font-['Forum']">
                Request a Private Consultation
              </div>
            </div>
            <div className="w-full justify-center text-[#f4f3f3] text-sm sm:text-base md:text-lg font-normal font-['Roobert'] leading-relaxed md:leading-[28.80px]">
              Tell us about your investment objectives, and a member of our team
              will be in touch to discuss how MG Asset Management may support
              your long term financial strategy.
            </div>
          </div>

          {/* Middle Image - Hidden on mobile */}
          <div className="hidden xl:block xl:w-1/5 absolute bottom-0 right-0 left-0  m-auto">
            <img
              className="w-full h-full object-cover"
              src={require("../assets/images/home/img_05.png")}
              alt="Consultation"
            />
          </div>

          {/* Right Form */}
          <div className="w-full xl:w-2/5 p-6 md:p-8 lg:p-12 bg-white rounded-[4px] flex flex-col justify-start items-start gap-6 md:gap-8">
            {/* Full Name */}
            <div className="w-full flex flex-col justify-start items-start gap-2">
              <label className="w-full text-[#3a4f66] text-sm md:text-lg font-normal font-['Roobert']">
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={name}
                onChange={handleConsultationChange}
                placeholder="Enter Full Name"
                className="w-full px-3 md:px-4 py-3 md:py-[18px] bg-[#f4f3f3] rounded-[4px] text-gray-500 text-sm md:text-base font-normal font-['Roobert'] placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#395645]"
              />
              {consultationErrors.name && (
                <span className="text-danger text-xs">{t(consultationErrors.name)}</span>
              )}
            </div>

            {/* Email and Phone */}
            <div className="w-full flex flex-col sm:flex-row justify-start items-start gap-6">
              <div className="w-full sm:flex-1 flex flex-col justify-start items-start gap-2">
                <label className="w-full text-[#3a4f66] text-sm md:text-lg font-normal font-['Roobert']">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={handleConsultationChange}
                  placeholder="Enter Email Address"
                  className="w-full px-3 md:px-4 py-3 md:py-[18px] bg-[#f4f3f3] rounded-[4px] text-gray-500 text-sm md:text-base font-normal font-['Roobert'] placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#395645]"
                />
                {consultationErrors.email && (
                  <span className="text-danger text-xs">{t(consultationErrors.email)}</span>
                )}
              </div>
              <div className="w-full sm:flex-1 flex flex-col justify-start items-start gap-2">
                <label className="w-full text-[#3a4f66] text-sm md:text-lg font-normal font-['Roobert']">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={phone}
                  onChange={handleConsultationChange}
                  placeholder="Enter Phone Number"
                  className="w-full px-3 md:px-4 py-3 md:py-[18px] bg-[#f4f3f3] rounded-[4px] text-gray-500 text-sm md:text-base font-normal font-['Roobert'] placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#395645]"
                />
                {consultationErrors.phone && (
                  <span className="text-danger text-xs">{t(consultationErrors.phone)}</span>
                )}
              </div>
            </div>

            {/* Message */}
            <div className="w-full flex flex-col justify-start items-start gap-2">
              <label className="w-full text-[#3a4f66] text-sm md:text-lg font-normal font-['Roobert']">
                Message
              </label>
              <textarea
                name="message"
                value={message}
                onChange={handleConsultationChange}
                placeholder="Enter Message"
                rows={5}
                className="w-full px-3 md:px-4 py-3 md:py-[18px] bg-[#f4f3f3] rounded-[4px] text-gray-500 text-sm md:text-base font-normal font-['Roobert'] placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#395645] resize-none"
              />
              {consultationErrors.message && (
                <span className="text-danger text-xs">{t(consultationErrors.message)}</span>
              )}
            </div>

            {/* Submit Button */}
            <button 
              className="primary_btn" 
              onClick={handleConsultationSubmit}
              disabled={loader}
            >
              {loader ? t("SENDING") : t("SEND_ENQUIRY")}
            </button>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("PRIVATE_CONSULTATION")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-5">
          <i className="bi bi-check-circle-fill text-success" style={{ fontSize: "4rem" }}></i>
          <h3 className="mt-4 font-['Forum'] text-[#395645]">{t("THANK_YOU")}</h3>
          <p className="text-[#3a4f66] mt-2">
            {t("CONSULTATION_SUCCESS_MSG", "Your request for a private consultation has been sent successfully. Our team will get back to you shortly.")}
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-center border-0 pb-4">
          <Button 
            className="px-5 py-2 rounded-[4px] bg-[#395645] border-0" 
            onClick={() => setShowSuccessModal(false)}
          >
            {t("CLOSE")}
          </Button>
        </Modal.Footer>
      </Modal>

      <FooterNew />
      <BackToTop />
    </main>
  );
}
